import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc, runTransaction } from 'firebase/firestore';
import { auth, db } from '../src/firebaseConfig';
import { resolveApiBaseUrl } from '../src/utils/apiBase';
import { LogEntry } from '../src/types';
import { isFirebaseMockModeEnabled } from '../src/utils/firebaseMockMode';
import { normalizeReservationOperation } from '../src/utils/reservationOperation.service';

const FIRESTORE_ACCESS_ERROR =
  'Your account is signed in, but Chronovela could not access your projects.';

interface UserData {
  tokens: number; // legacy
  subscriptionTier: 'free' | 'pro';
  email: string;
  standardCredits?: number; // legacy
  longCredits?: number; // legacy
  epicCredits?: number; // legacy
  rewriteCredits?: number; // legacy
  rewriteCreditsBalance?: number;
  manuscriptCreditBalances?: {
    short_manuscript?: number;
    standard_manuscript?: number;
    large_manuscript?: number;
    epic_manuscript?: number;
  };
  legalAccepted?: boolean;
  legalAcceptedAt?: any;
  legalAcceptedVersion?: string;
  termsAccepted?: boolean;
  privacyAccepted?: boolean;
  refundAccepted?: boolean;
  aiDisclaimerAccepted?: boolean;
}

import { parseReservationError } from '../src/utils/generationRunContext';
import type { ReserveTokensResult } from '../src/hooks/useGenerationLifecycle';

interface FirebaseContextType {
  user: User | null;
  userData: UserData | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  reserveTokens: (runId: string, projectId: string, chapterNum: number, operation: string, amount: number, entitlement?: any) => Promise<ReserveTokensResult>;
  consumeTokenReservation: (runId: string) => Promise<boolean>;
  releaseTokenReservation: (runId: string, failureReason?: string) => Promise<boolean>;
  updateUserData: (data: Partial<UserData>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  applyManuscriptCreditToProject: (projectId: string, tier: string, isMock?: boolean) => Promise<any | null>;
  loading: boolean;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export function FirebaseProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [initializationErrorCode, setInitializationErrorCode] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setInitializationError(null);
    setInitializationErrorCode(null);
    if (isFirebaseMockModeEnabled()) {
      const mockUid = 'CpX6LG47IPVv9cKrwmxzpiMiRji1';
      const mockUser = {
        uid: mockUid,
        email: 'skaldhaven@gmail.com',
        displayName: 'Admin Test',
        getIdToken: async () => 'mock_user_token'
      } as any;
      setUser(mockUser);
      
      const fetchMockData = async () => {
        try {
          const userRef = doc(db, 'users', mockUid);
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            setUserData(snap.data() as UserData);
          } else {
            const newProfile: UserData = {
              email: 'admin_test@chronovela.app',
              tokens: 100,
              subscriptionTier: 'free',
              standardCredits: 0,
              longCredits: 0,
              epicCredits: 0,
              rewriteCredits: 0
            };
            await setDoc(userRef, { ...newProfile, createdAt: serverTimestamp() });
            setUserData(newProfile);
          }
        } catch (error: any) {
          console.error('[Chronovela Firestore] mock profile read failed', error?.code || 'unknown', error?.message || error);
          setInitializationError(FIRESTORE_ACCESS_ERROR);
          setInitializationErrorCode(error?.code || 'unknown');
        } finally {
          setLoading(false);
        }
      };
      fetchMockData();
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currUser) => {
      setUser(currUser);
      if (currUser) {
        const userRef = doc(db, 'users', currUser.uid);
        if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
          console.log(`[Diagnostic Operation] READ starting: Users collection | Path: "users/${currUser.uid}" | Auth UID exists: true | UID: "${currUser.uid}"`);
        }
        try {
          const snap = await getDoc(userRef);
          if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
            console.log(`[Diagnostic Operation] READ success: Users collection | Path: "users/${currUser.uid}" | Document exists: ${snap.exists()}`);
          }
          if (snap.exists()) {
            setUserData(snap.data() as UserData);
          } else {
            // Initialize fresh user with some tokens and credits
            const newProfile: UserData = {
              email: currUser.email || '',
              tokens: 100, // starting tokens
              subscriptionTier: 'free',
              standardCredits: 0,
              longCredits: 0,
              epicCredits: 0,
              rewriteCredits: 0
            };
            if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
              console.log(`[Diagnostic Operation] WRITE starting (Initialize): Users collection | Path: "users/${currUser.uid}" | Auth UID: "${currUser.uid}"`);
            }
            await setDoc(userRef, { ...newProfile, createdAt: serverTimestamp() });
            if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
              console.log(`[Diagnostic Operation] WRITE success: Users collection | Path: "users/${currUser.uid}"`);
            }
            setUserData(newProfile);
          }
        } catch (err: any) {
          console.error(
            `[Chronovela Firestore] profile read failed | path=users/${currUser.uid} | code=${err?.code || 'unknown'} | message=${err?.message || err}`,
          );
          if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
            console.error(`[Diagnostic Operation] FAILED Users collection | Path: "users/${currUser.uid}" | Error Code: "${err.code}" | Message: "${err.message}"`);
          }
          setInitializationError(
            err?.code === 'permission-denied' ? FIRESTORE_ACCESS_ERROR : 'Chronovela could not connect to its data service.',
          );
          setInitializationErrorCode(err?.code || 'unknown');
        } finally {
          setLoading(false);
        }
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [retryCount]);
  const login = async () => {
    if (isFirebaseMockModeEnabled()) {
      const mockUser = {
        uid: 'CpX6LG47IPVv9cKrwmxzpiMiRji1',
        email: 'skaldhaven@gmail.com',
        displayName: 'Admin Test',
        getIdToken: async () => 'mock_user_token'
      } as any;
      setUser(mockUser);
      
      const userRef = doc(db, 'users', 'CpX6LG47IPVv9cKrwmxzpiMiRji1');
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        setUserData(snap.data() as UserData);
      } else {
        const newProfile: UserData = {
          email: 'admin_test@chronovela.app',
          tokens: 100,
          subscriptionTier: 'free',
          standardCredits: 0,
          longCredits: 0,
          epicCredits: 0,
          rewriteCredits: 0
        };
        await setDoc(userRef, { ...newProfile, createdAt: serverTimestamp() });
        setUserData(newProfile);
      }
      return;
    }
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const reserveTokens = async (runId: string, projectId: string, chapterNum: number, operation: string, amount: number, entitlement?: any): Promise<ReserveTokensResult> => {
    if (!user || !userData) return { ok: false, error: { kind: 'unknown', message: 'Not signed in.' } };
    
    const normalizedOperation = normalizeReservationOperation(operation);
    if (!normalizedOperation) {
      return {
        ok: false,
        error: {
          kind: 'unknown',
          reason: 'invalid_operation',
          message: 'Chronovela could not start this generation because the reservation request was invalid. No credit was used. Please try again.',
        },
      };
    }

    if (normalizedOperation === 'rewrite') {
      const balance = userData.rewriteCreditsBalance || 0;
      if (balance < amount) {
        return { ok: false, error: { kind: 'insufficient_credits', message: 'You need Rewrite Credits to perform a rewrite.' } };
      }
    } else if (normalizedOperation === 'manuscript_generation' || normalizedOperation === 'free_preview') {
      // Entitlement guard is checked on the backend during reservation.
    } else {
      if ((userData.tokens || 0) < amount) {
        return { ok: false, error: { kind: 'insufficient_credits', message: 'You need backend AI credits to continue.' } };
      }
    }
    
    try {
      const idToken = await user.getIdToken();
      const API_BASE = resolveApiBaseUrl();

      console.log("[Token Reserve Request]", {
        projectId,
        runId,
        chapterNum,
        operation,
        amount,
        ...entitlement
      });

      const response = await fetch(`${API_BASE}/api/tokens/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ projectId, runId, chapterNum, operation: normalizedOperation, amount, ...entitlement })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const parsed = parseReservationError(response.status, errData);
        console.error('TOKEN_RESERVATION_REJECTED:', parsed.kind, errData);
        return { ok: false, error: parsed };
      }

      const { newBalance } = await response.json();
      if (newBalance !== undefined) {
         setUserData({ ...userData, tokens: newBalance });
      }
      return { ok: true };
    } catch (e) {
      console.error("Error reserving tokens:", e);
      return {
        ok: false,
        error: {
          kind: 'unknown',
          reason: 'network_error',
          message: 'Chronovela could not verify your manuscript credit. No credit was used. Please try again.',
        },
      };
    }
  };

  const consumeTokenReservation = async (runId: string): Promise<boolean> => {
    if (!user || !userData) return false;
    try {
      const idToken = await user.getIdToken();
      const API_BASE = resolveApiBaseUrl();

      const response = await fetch(`${API_BASE}/api/tokens/consume-reservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ runId })
      });

      return response.ok;
    } catch (e) {
      console.error("Error consuming token reservation:", e);
      return false;
    }
  };

  const releaseTokenReservation = async (runId: string, failureReason?: string): Promise<boolean> => {
    if (!user || !userData) return false;
    try {
      const idToken = await user.getIdToken();
      const API_BASE = resolveApiBaseUrl();

      const response = await fetch(`${API_BASE}/api/tokens/release-reservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ runId, failureReason })
      });

      if (!response.ok) {
         console.warn("Release failed on backend");
         return false;
      }

      const { newBalance } = await response.json();
      if (newBalance !== undefined) {
         setUserData({ ...userData, tokens: newBalance });
      }
      return true;
    } catch (e) {
      console.error("Error releasing tokens:", e);
      return false;
    }
  };

  const updateUserData = async (data: Partial<UserData>): Promise<void> => {
    if (!user || !userData) return;
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, data);
    setUserData(prev => prev ? { ...prev, ...data } : null);
  };

  const refreshUserData = async (): Promise<void> => {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      setUserData(snap.data() as UserData);
    }
  };

  const applyManuscriptCreditToProject = async (projectId: string, tier: string, isMock: boolean = false): Promise<any | null> => {
    if (!user || !userData) return null;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const projectRef = doc(db, 'users', user.uid, 'projects', projectId);
      const stateRef = doc(db, 'users', user.uid, 'projects', projectId, 'state', 'current');
      
      const existingProjectSnap = await getDoc(projectRef);
      if (existingProjectSnap.exists()) {
         const existingProject = existingProjectSnap.data();
         const isPaidTier = existingProject.creditTier === 'standard_manuscript' || existingProject.creditTier === 'large_manuscript' || existingProject.creditTier === 'epic_manuscript';
         if (isPaidTier && existingProject.exportUnlocked) {
            console.log(`[Apply Credit Idempotent] Existing paid entitlement found; no credit consumed.`);
            return {
              creditTier: existingProject.creditTier,
              maxChaptersAllowed: existingProject.maxChaptersAllowed || 1,
              exportUnlocked: true,
              paidEntitlementId: existingProject.paidEntitlementId || `legacy-${projectId}`,
              entitlementAppliedAt: existingProject.entitlementAppliedAt || new Date().toISOString(),
              rewriteCreditsIncluded: existingProject.rewriteCreditsIncluded || 0,
              rewriteCreditsUsed: existingProject.rewriteCreditsUsed || 0
            };
         }
      }

      const appliedAt = new Date().toISOString();
      const paidEntitlementId = isMock ? `mock_${tier}_${projectId}` : `credit_${tier}_${projectId}_${Date.now()}`;
      const maxChapters = tier === 'standard_manuscript' ? 20 : tier === 'large_manuscript' ? 30 : tier === 'epic_manuscript' ? 40 : 1;
      const rewritesIncluded = tier === 'standard_manuscript' ? 5 : tier === 'large_manuscript' ? 15 : tier === 'epic_manuscript' ? 35 : 0;
      
      const entitlementUpdate = {
        creditTier: tier,
        maxChaptersAllowed: maxChapters,
        exportUnlocked: true,
        paidEntitlementId,
        entitlementAppliedAt: appliedAt,
        rewriteCreditsIncluded: rewritesIncluded,
        rewriteCreditsUsed: 0
      };

      console.log("[APPLY CREDIT WRITE PATH]", {
        uid: user.uid,
        projectId,
        projectRootPath: projectRef.path,
        stateCurrentPath: stateRef.path,
        tier
      });

      await runTransaction(db, async (tx) => {
        const userSnap = await tx.get(userRef);
        if (!userSnap.exists()) throw new Error("User not found");
        
        const data = userSnap.data() as UserData;
        const balances = data.manuscriptCreditBalances || {};
        const currentBal = balances[tier as keyof typeof balances] || 0;
        
        if (!isMock) {
          if (currentBal < 1) {
            throw new Error(`Insufficient ${tier} credits`);
          }
          // decrement balance
          tx.update(userRef, {
            [`manuscriptCreditBalances.${tier}`]: currentBal - 1
          });
        }
        
        // Ensure project root updates
        tx.set(projectRef, entitlementUpdate, { merge: true });
        
        // Ensure state/current updates
        tx.set(stateRef, { storyBible: entitlementUpdate }, { merge: true });
      });

      // Verification Step
      const verificationSnap = await getDoc(projectRef);
      const verificationData = verificationSnap.data() || {};
      
      if (verificationData.creditTier !== tier || !verificationData.paidEntitlementId) {
        console.error(`[ENTITLEMENT VERIFY FAILED] Project root missing paid entitlement.`);
        
        // Compensating transaction
        if (!isMock) {
            await runTransaction(db, async (tx) => {
               const uSnap = await tx.get(userRef);
               if (uSnap.exists()) {
                 const data = uSnap.data() as UserData;
                 const balances = data.manuscriptCreditBalances || {};
                 const currentBal = balances[tier as keyof typeof balances] || 0;
                 tx.update(userRef, {
                   [`manuscriptCreditBalances.${tier}`]: currentBal + 1
                 });
               }
            });
            await refreshUserData();
        }

        throw new Error("Credit apply failed. Project entitlement did not persist. Credit refunded.");
      }

      console.log(`[TRACE 1] Firestore root after transaction:`, {
        path: projectRef.path,
        projectId,
        creditTier: verificationData.creditTier,
        exportUnlocked: verificationData.exportUnlocked,
        paidEntitlementId: verificationData.paidEntitlementId,
        maxChaptersAllowed: verificationData.maxChaptersAllowed,
        manuscriptBalance: userData.manuscriptCreditBalances?.[tier as keyof typeof userData.manuscriptCreditBalances] ? userData.manuscriptCreditBalances[tier as keyof typeof userData.manuscriptCreditBalances]! - 1 : 0
      });

      const verifiedEntitlement = {
        creditTier: verificationData.creditTier,
        maxChaptersAllowed: verificationData.maxChaptersAllowed,
        exportUnlocked: verificationData.exportUnlocked,
        paidEntitlementId: verificationData.paidEntitlementId,
        entitlementAppliedAt: verificationData.entitlementAppliedAt,
        rewriteCreditsIncluded: verificationData.rewriteCreditsIncluded,
        rewriteCreditsUsed: verificationData.rewriteCreditsUsed
      };

      // Update local state immediately
      if (!isMock) {
         setUserData(prev => {
            if (!prev) return prev;
            const b = prev.manuscriptCreditBalances || {};
            return {
              ...prev,
              manuscriptCreditBalances: {
                 ...b,
                 [tier as keyof typeof b]: Math.max(0, (b[tier as keyof typeof b] || 0) - 1)
              }
            };
         });
      }

      return verifiedEntitlement;
    } catch (err: any) {
      console.error("Failed to apply credit", err);
      return null;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: '#111827', color: 'white', fontFamily: 'sans-serif' }}>
        <h2>Loading Chronovela...</h2>
      </div>
    );
  }

  if (initializationError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: '#111827', color: 'white', fontFamily: 'sans-serif', gap: '1rem', padding: '1.5rem' }}>
        <h2 style={{ textAlign: 'center', maxWidth: '32rem' }}>{initializationError}</h2>
        {initializationErrorCode && import.meta.env.DEV && (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Firebase error code: {initializationErrorCode}</p>
        )}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => setRetryCount(c => c + 1)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', borderRadius: '0.25rem', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
          >
            Try Again
          </button>
          <button
            onClick={() => logout().catch(console.error)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#334155', color: 'white', borderRadius: '0.25rem', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <FirebaseContext.Provider value={{ user, userData, login, logout, reserveTokens, consumeTokenReservation, releaseTokenReservation, updateUserData, refreshUserData, applyManuscriptCreditToProject, loading }}>
      {children}
    </FirebaseContext.Provider>
  );
}

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};
